import { z } from 'zod';

const SubscriptionLineItemDataSchema = z.object({
  id: z.string(),
  quantity: z.number(),
  price: z.object({
    id: z.string(),
  }),
});

const CardPaymentMethodDataSchema = z.object({
  type: z.literal('card'),
  card: z.object({
    brand: z.string(),
    last4: z.string(),
    expMonth: z.number(),
    expYear: z.number(),
  }),
});

const CustomPaymentMethodDataSchema = z.object({
  type: z.literal('custom'),
});

const AmazonPayPaymentMethodDataSchema = z.object({
  type: z.literal('amazon_pay'),
});

const GooglePayPaymentMethodDataSchema = z.object({
  type: z.literal('google_pay'),
});

const ApplePayPaymentMethodDataSchema = z.object({
  type: z.literal('apple_pay'),
});

const AcssDebitPaymentMethodDataSchema = z.object({
  type: z.literal('acss_debit'),
});

const UsBankAccountPaymentMethodDataSchema = z.object({
  type: z.literal('us_bank_account'),
  usBankAccount: z
    .object({
      accountHolderType: z.string().optional(),
      accountType: z.string().optional(),
      bankName: z.string().optional(),
      last4: z.string(),
    })
    .optional(),
});

const PaymentMethodDataAlgebraicSchema = z.union([
  CardPaymentMethodDataSchema,
  CustomPaymentMethodDataSchema,
  AcssDebitPaymentMethodDataSchema,
  UsBankAccountPaymentMethodDataSchema,
  AmazonPayPaymentMethodDataSchema,
  GooglePayPaymentMethodDataSchema,
  ApplePayPaymentMethodDataSchema,
]);

const PaymentMethodDataSchema = z
  .object({
    id: z.string(),
  })
  .and(PaymentMethodDataAlgebraicSchema);

export type StripePaymentMethodData = z.infer<typeof PaymentMethodDataSchema>;

const UpcomingInvoiceDataSchema = z.object({
  amountDue: z.number(),
  currency: z.string(),
  periodStart: z.number(),
  periodEnd: z.number(),
});

const LastPaidInvoiceDataSchema = z.object({
  id: z.string(),
  number: z.string(),
  total: z.number(),
  currency: z.string(),
  created: z.number(),
  periodStart: z.number(),
  periodEnd: z.number(),
});

const SubscriptionDataSchema = z.object({
  id: z.string(),
  status: z.string(),
  currency: z.string(),
  interval: z.literal('month'),
  intervalCount: z.number(),
  billingCycleAnchor: z.number(),
  billingDayOfMonth: z.number(),
  amount: z.number(),
  items: z.array(SubscriptionLineItemDataSchema),
});

export const StripeCustomerDataSchema = z.object({
  customer: z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
    balance: z.number(),
    currency: z.string(),
    metadata: z.object({
      customer_reference_id: z.string(),
      is_house: z.string().optional(),
      parent_puid: z.string().optional(),
      pays_for: z.string().optional(),
    }),
  }),
  paymentMethod: PaymentMethodDataSchema.optional(),
  subscription: SubscriptionDataSchema.optional(),
  lastPaidInvoice: LastPaidInvoiceDataSchema.optional(),
  upcomingInvoice: UpcomingInvoiceDataSchema.optional(),
});

export type StripeCustomerData = z.infer<typeof StripeCustomerDataSchema>;
